import { Home, Dumbbell, Target } from 'lucide-react'
import { Link } from 'react-router-dom'
import Layout from '@/components/layout/layout'
import CustomersEntity from '@/entities/api/customers/customer'

const ProductNotFound = ({
	customer,
	token,
}: {
	customer: CustomersEntity | null
	token: string | null
}) => {
	return (
		<Layout customer={customer} token={token}>
			<div className='min-h-screen bg-gradient-to-br from-slate-50 via-gray-50 to-red-50 flex items-center justify-center px-4 sm:px-6 lg:px-8 relative overflow-hidden'>
				<div className='absolute inset-0 overflow-hidden'>
					<div className='absolute -top-20 -right-20 w-80 h-80 bg-gradient-to-br from-red-100 to-red-200 rounded-full opacity-30 animate-pulse'></div>
					<div className='absolute -bottom-32 -left-32 w-96 h-96 bg-gradient-to-tr from-gray-100 to-slate-200 rounded-full opacity-20 animate-pulse delay-1000'></div>
					<div className='absolute top-1/3 right-1/4 w-32 h-32 bg-gradient-to-bl from-red-200 to-red-300 rounded-full opacity-20 animate-bounce delay-500'></div>
				</div>

				<div className='relative z-10 max-w-2xl w-full text-center'>
					<div className='relative mb-8 sm:mb-12'>
						<div className='inline-flex items-center justify-center w-32 h-32 sm:w-40 sm:h-40 bg-gradient-to-br from-red-500 to-red-700 rounded-3xl shadow-2xl mb-6 relative overflow-hidden'>
							<div className='absolute inset-0 bg-gradient-to-tr from-white/10 to-transparent'></div>

							<div className='relative z-10 flex items-center justify-center'>
								<Dumbbell
									className='w-16 h-16 sm:w-20 sm:h-20 text-white animate-pulse'
									strokeWidth={1.5}
								/>
							</div>

							<div className='absolute -top-2 -right-2 w-8 h-8 bg-white/20 rounded-full flex items-center justify-center animate-bounce delay-300'>
								<Target className='w-4 h-4 text-white' strokeWidth={2} />
							</div>
						</div>

						<div className='w-24 h-1 bg-gradient-to-r from-red-500 to-red-600 mx-auto rounded-full'></div>
					</div>

					<div className='mb-12 sm:mb-16'>
						<h1 className='text-4xl sm:text-5xl lg:text-6xl font-black text-gray-900 mb-6 tracking-tight'>
							<span className='bg-gradient-to-r from-gray-900 via-gray-800 to-red-600 bg-clip-text text-transparent'>
								Producto no
							</span>
							<br />
							<span className='text-red-600'>encontrado</span>
						</h1>

						<div className='space-y-3 sm:space-y-4'>
							<p className='text-lg sm:text-xl lg:text-2xl text-gray-600 font-medium leading-relaxed max-w-xl mx-auto'>
								El producto que buscas no existe o ha sido eliminado de nuestro
								catálogo.
							</p>

							<p className='text-base sm:text-lg text-gray-500 leading-relaxed max-w-lg mx-auto'>
								No te preocupes, tenemos miles de productos deportivos
								esperándote.
							</p>
						</div>
					</div>

					<div className='mb-12 sm:mb-16'>
						<Link
							to='/'
							className='group inline-flex items-center justify-center px-8 sm:px-12 py-4 sm:py-5 bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white font-bold text-lg sm:text-xl rounded-2xl shadow-xl hover:shadow-2xl transform hover:scale-105 transition-all duration-300 relative overflow-hidden'
						>
							<div className='absolute inset-0 bg-gradient-to-r from-white/0 via-white/10 to-white/0 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700'></div>

							<Home
								className='w-6 h-6 sm:w-7 sm:h-7 mr-3 group-hover:scale-110 transition-transform duration-200'
								strokeWidth={2}
							/>
							<span className='relative z-10'>Volver al inicio</span>
						</Link>
					</div>

					<div className='grid grid-cols-2 sm:grid-cols-4 gap-4 sm:gap-6 max-w-2xl mx-auto'>
						{[
							{
								name: 'Suplementos',
								icon: '💊',
								gradient: 'from-blue-500 to-blue-600',
								shadowColor: 'shadow-blue-200',
							},
							{
								name: 'Ropa Deportiva',
								icon: '👕',
								gradient: 'from-green-500 to-green-600',
								shadowColor: 'shadow-green-200',
							},
							{
								name: 'Accesorios',
								icon: '🎽',
								gradient: 'from-purple-500 to-purple-600',
								shadowColor: 'shadow-purple-200',
							},
							{
								name: 'Entrenamientos',
								icon: '🏋️',
								gradient: 'from-orange-500 to-orange-600',
								shadowColor: 'shadow-orange-200',
							},
						].map((category, index) => (
							<div
								key={index}
								className={`group p-4 sm:p-6 bg-white rounded-2xl shadow-lg ${category.shadowColor} hover:shadow-xl transition-all duration-300 hover:scale-105 border border-gray-100`}
							>
								<div
									className={`w-12 h-12 sm:w-16 sm:h-16 bg-gradient-to-br ${category.gradient} rounded-xl flex items-center justify-center mx-auto mb-3 sm:mb-4 group-hover:rotate-6 transition-transform duration-300`}
								>
									<span className='text-xl sm:text-2xl'>{category.icon}</span>
								</div>
								<h3 className='text-sm sm:text-base font-semibold text-gray-800 leading-tight'>
									{category.name}
								</h3>
							</div>
						))}
					</div>

					<div className='mt-12 sm:mt-16 pt-8 sm:pt-12 border-t border-gray-200'>
						<div className='grid grid-cols-3 gap-6 sm:gap-8 max-w-lg mx-auto'>
							<div className='text-center'>
								<div className='text-2xl sm:text-3xl font-black text-red-600 mb-2'>
									500+
								</div>
								<div className='text-sm sm:text-base text-gray-600 font-medium'>
									Productos
								</div>
							</div>
							<div className='text-center'>
								<div className='text-2xl sm:text-3xl font-black text-red-600 mb-2'>
									24/7
								</div>
								<div className='text-sm sm:text-base text-gray-600 font-medium'>
									Soporte
								</div>
							</div>
							<div className='text-center'>
								<div className='text-2xl sm:text-3xl font-black text-red-600 mb-2'>
									⭐ 4.9
								</div>
								<div className='text-sm sm:text-base text-gray-600 font-medium'>
									Valoración
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		</Layout>
	)
}

export default ProductNotFound
